import { Search, Trash2, Filter, Pencil, UserCheck } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { StatusBadge, StatusActions } from '@/components/admin/StatusBadge';

const ApplicationsTab = ({
  filteredApps, searchTerm, setSearchTerm,
  appStatusFilter, setAppStatusFilter,
  selectedAppIds, setSelectedAppIds, toggleAppSelect, toggleAllApps, batchAppAction,
  colConfig, appSort, toggleSort,
  appVisibleCount, setAppVisibleCount,
  handleStatusChange, handleDelete, rejectionCodes,
  setSelectedApp, setEditingApp,
  isLoading, stats,
}) => {
  return (
    <>
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9B9589]" size={16} />
          <Input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search by name, email, PAN..." className="input-ainaa pl-11 h-11" data-testid="search-input" />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-[#9B9589]" />
          {['all', 'verification_pending', 'approved', 'rejected'].map(f => (
            <button key={f} onClick={() => setAppStatusFilter(f)}
              className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-all ${appStatusFilter === f ? 'bg-[#0F1B2D] text-[#F5F3EE]' : 'border border-[#D8D3CB] text-[#9B9589] hover:border-[#0F1B2D]'}`}
              data-testid={`filter-${f}`}
            >
              {f === 'all' ? 'All' : f === 'verification_pending' ? 'Pending' : f === 'approved' ? 'Approved' : 'Rejected'}
            </button>
          ))}
        </div>
        <colConfig.ColumnToggle />
      </div>

      {/* Batch toolbar */}
      {selectedAppIds.size > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-[#0F1B2D] text-[#F5F3EE]" data-testid="app-batch-toolbar">
          <span className="text-xs font-bold">{selectedAppIds.size} selected</span>
          <button onClick={() => batchAppAction('approved')} className="px-3 py-1.5 text-[10px] font-bold uppercase bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="batch-approve">Approve</button>
          <button onClick={() => batchAppAction('rejected')} className="px-3 py-1.5 text-[10px] font-bold uppercase bg-red-600 hover:bg-red-700 text-white" data-testid="batch-reject">Reject</button>
          <button onClick={() => batchAppAction('delete')} className="px-3 py-1.5 text-[10px] font-bold uppercase bg-red-800 hover:bg-red-900 text-white" data-testid="batch-delete-apps">Delete</button>
          <button onClick={() => setSelectedAppIds(new Set())} className="ml-auto px-3 py-1.5 text-[10px] font-bold uppercase border border-white/30 hover:bg-white/10">Clear</button>
        </div>
      )}

      <div className="border border-[#D8D3CB] bg-white overflow-x-auto">
        <table className="w-full" data-testid="applications-table" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr className="border-b-2 border-[#D8D3CB]">
              <th className="px-3 py-3" style={{ width: '40px' }}>
                <input type="checkbox"
                  checked={filteredApps.length > 0 && selectedAppIds.size === filteredApps.slice(0, appVisibleCount).length}
                  onChange={() => toggleAllApps(filteredApps.slice(0, appVisibleCount).map(e => e.id))}
                  className="w-3.5 h-3.5 accent-[#C4A35A] cursor-pointer" data-testid="app-select-all"
                />
              </th>
              {colConfig.visibleCols.map(col => (
                <colConfig.ResizableHeader key={col.key} col={col} sortKey={col.key} currentSort={appSort} onSort={toggleSort} />
              ))}
              <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[#9B9589]" style={{ width: '100px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={colConfig.visibleCols.length + 2} className="text-center py-12 text-[#9B9589] font-medium">Loading...</td></tr>
            ) : filteredApps.length === 0 ? (
              <tr><td colSpan={colConfig.visibleCols.length + 2} className="text-center py-12 text-[#9B9589] font-medium">No applications found</td></tr>
            ) : (
              filteredApps.slice(0, appVisibleCount).map((entry) => {
                const st = entry.status || 'verification_pending';
                return (
                  <tr key={entry.id} className={`border-b border-[#D8D3CB]/50 hover:bg-[#EDECEA]/30 cursor-pointer ${st === 'rejected' ? 'bg-red-50/20' : st === 'approved' ? 'bg-emerald-50/20' : ''}`} onClick={() => setSelectedApp(entry)}>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox"
                        checked={selectedAppIds.has(entry.id)}
                        onChange={() => toggleAppSelect(entry.id)}
                        className="w-3.5 h-3.5 accent-[#C4A35A] cursor-pointer"
                      />
                    </td>
                    {colConfig.visibleCols.map(col => {
                      const w = colConfig.colWidths[col.key];
                      const style = w ? { width: `${w}px`, minWidth: `${w}px` } : {};
                      switch (col.key) {
                        case 'application_id':
                          return <td key={col.key} className="px-4 py-3" style={style}><span className="text-[11px] font-mono font-bold text-[#C4A35A] truncate block">{entry.application_id || '—'}</span></td>;
                        case 'name':
                          return <td key={col.key} className="px-4 py-3" style={style}><span className="font-semibold text-sm text-[#0F1B2D] truncate block">{entry.name}</span></td>;
                        case 'email':
                          return <td key={col.key} className="px-4 py-3" style={style}><span className="text-sm text-[#4A4640] truncate block">{entry.email}</span></td>;
                        case 'profession':
                          return <td key={col.key} className="px-4 py-3" style={style}><span className="text-sm text-[#4A4640] truncate block">{entry.profession}</span></td>;
                        case 'pan':
                          return <td key={col.key} className="px-4 py-3" style={style}>{entry.pan ? <span className="text-xs font-mono font-semibold text-[#0F1B2D]">{entry.pan}</span> : <span className="text-[#D8D3CB]">—</span>}</td>;
                        case 'experience_years':
                          return <td key={col.key} className="px-4 py-3" style={style}>{entry.experience_years != null ? <span className="text-xs font-semibold text-[#4A4640]">{entry.experience_years}y</span> : <span className="text-[#D8D3CB]">—</span>}</td>;
                        case 'country':
                          return <td key={col.key} className="px-4 py-3" style={style}>{entry.country ? <span className="text-xs text-[#4A4640]">{entry.country}</span> : <span className="text-[#D8D3CB]">—</span>}</td>;
                        case 'status':
                          return <td key={col.key} className="px-4 py-3" style={style}><StatusBadge status={st} /></td>;
                        default:
                          return <td key={col.key} className="px-4 py-3" style={style}><span className="text-sm text-[#4A4640]">{entry[col.key] ?? '—'}</span></td>;
                      }
                    })}
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setEditingApp(entry)} className="p-1.5 text-[#9B9589] hover:text-[#C4A35A] transition-colors" title="Edit" data-testid={`edit-app-${entry.id}`}><Pencil size={14} /></button>
                        <StatusActions entry={entry} onStatusChange={handleStatusChange} rejectionCodes={rejectionCodes} />
                        <button onClick={() => handleDelete(entry.id)} className="p-1.5 text-[#D8D3CB] hover:text-red-500 transition-colors" data-testid={`delete-${entry.id}`}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {filteredApps.length > appVisibleCount && (
          <div className="border-t border-[#D8D3CB] bg-[#EDECEA]/50 p-3 text-center">
            <button onClick={() => setAppVisibleCount(c => c + 50)} className="px-5 py-2 text-[11px] font-bold text-[#0F1B2D] uppercase tracking-wider border border-[#D8D3CB] hover:border-[#0F1B2D] bg-white transition-all inline-flex items-center gap-2" data-testid="apps-show-more">
              Show More ({filteredApps.length - appVisibleCount} remaining)
            </button>
          </div>
        )}
        {filteredApps.length > 0 && (
          <div className="border-t border-[#D8D3CB] bg-white px-4 py-2 text-right">
            <span className="text-[10px] text-[#9B9589] font-medium">Showing {Math.min(appVisibleCount, filteredApps.length)} of {filteredApps.length}</span>
          </div>
        )}
      </div>

      {/* Rejection remarks for filtered rejected */}
      {appStatusFilter === 'rejected' && rejectionCodes.length > 0 && (
        <div className="border border-[#D8D3CB] bg-white p-5 mt-6">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#9B9589] mb-3">Rejection Reason Codes</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {rejectionCodes.map((rc) => (
              <div key={rc.code} className="flex items-start gap-2">
                <span className="text-[10px] font-mono font-bold text-red-600 bg-red-50 px-1.5 py-0.5 border border-red-200 flex-shrink-0">{rc.code}</span>
                <span className="text-xs text-[#4A4640]">{rc.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Improvement areas */}
      {stats?.by_improvement_area?.length > 0 && (
        <div className="mt-6 border border-[#D8D3CB] bg-white p-6">
          <h3 className="text-sm font-bold text-[#0F1B2D] uppercase tracking-wider mb-4">Applications by Field</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {stats.by_improvement_area.map((item) => (
              <div key={item.area} className="text-center p-3 bg-[#EDECEA]">
                <p className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D]">{item.count}</p>
                <p className="text-[9px] font-bold text-[#9B9589] mt-1 capitalize uppercase tracking-wider">{item.area.replace(/-/g, ' ')}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default ApplicationsTab;
